/**
 * QuickFixModal — diagnoses a sub-Healthy profile (score < 70) and
 * surfaces one-click jumps to the editor tab that closes each gap.
 * Turns a passive "Needs attention" pill into a self-resolving worklist.
 */
import React, { useMemo } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { healthTier } from './helpers';

// Target counts the health score considers "complete". Mirrored from
// backend computation so the messaging stays in sync (any drift here
// only affects copy — the score itself is authoritative server-side).
const TARGET_BASELINES = 10;
const TARGET_WEIGHTS = 15;
const TARGET_ELASTICITIES = 10;
const RECENCY_FRESH_DAYS = 30;

export function buildFixes(profile, breakdown, stats) {
  const fixes = [];
  const baselinesN = (profile.kpi_baselines || []).length;
  const weightsN = (profile.lei_kpi_weights || []).length;
  const elasticitiesN = (profile.elasticities || []).length;
  const hasContext = !!(profile.llm_industry_context || '').trim();

  // ── Completeness gaps — ordered by biggest impact on the score.
  if (!hasContext) {
    fixes.push({
      severity: 'high',
      dimension: 'Completeness',
      headline: 'No LLM industry context written',
      detail: 'AI Coach and Insights fall back to generic language without this. ~20% of completeness score.',
      cta: 'Write industry context',
      tab: 'caps',
    });
  }
  if (baselinesN < TARGET_BASELINES) {
    fixes.push({
      severity: baselinesN === 0 ? 'high' : 'medium',
      dimension: 'Completeness',
      headline: `Only ${baselinesN} KPI baseline${baselinesN === 1 ? '' : 's'} of ${TARGET_BASELINES} recommended`,
      detail: 'Missing baselines fall back to global defaults — ROI calculations won\'t reflect this industry\'s cost structure. ~30% of completeness score.',
      cta: 'Add KPI baselines',
      tab: 'kpis',
    });
  }
  if (weightsN < TARGET_WEIGHTS) {
    fixes.push({
      severity: weightsN < 5 ? 'medium' : 'low',
      dimension: 'Completeness',
      headline: `${weightsN} LEI × KPI weight overrides (target ${TARGET_WEIGHTS})`,
      detail: 'Unspecified cells inherit the global weight matrix. Override the cells where this industry diverges most. ~25% of completeness score.',
      cta: 'Tune weight matrix',
      tab: 'matrix',
    });
  }
  if (elasticitiesN < TARGET_ELASTICITIES) {
    fixes.push({
      severity: elasticitiesN < 3 ? 'medium' : 'low',
      dimension: 'Completeness',
      headline: `${elasticitiesN} elasticity overrides (target ${TARGET_ELASTICITIES})`,
      detail: 'Tune per-measure elasticity for the levers this industry responds to differently. ~25% of completeness score.',
      cta: 'Add elasticities',
      tab: 'matrix',
    });
  }

  // ── Recency
  if ((breakdown.recency || 0) < 0.8) {
    const updatedAt = profile.updated_at || profile.created_at;
    let ageStr = 'a while';
    if (updatedAt) {
      try {
        const days = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000);
        ageStr = `${days} day${days === 1 ? '' : 's'}`;
      } catch { /* noop */ }
    }
    fixes.push({
      severity: (breakdown.recency || 0) < 0.3 ? 'high' : 'medium',
      dimension: 'Recency',
      headline: `Profile last updated ${ageStr} ago`,
      detail: `Industries refreshed within ${RECENCY_FRESH_DAYS} days score 100% on recency. A quick "Save changes" with any tweak resets the clock.`,
      cta: 'Open editor',
      tab: 'basics',
    });
  }

  // ── Adoption
  if ((stats.org_count || 0) === 0) {
    fixes.push({
      severity: 'high',
      dimension: 'Adoption',
      headline: '0 organizations assigned',
      detail: 'No tenant is using this industry. Either reassign tagged orgs from another vertical, or archive if this is a stale profile.',
      cta: 'Review tenants',
      altCta: 'Archive profile',
      onAlt: 'archive',
    });
  } else if ((breakdown.adoption || 0) < 0.4) {
    fixes.push({
      severity: 'low',
      dimension: 'Adoption',
      headline: 'Low adoption vs library leaders',
      detail: 'Only a small share of tagged tenants land here. If this is intentional (e.g. niche vertical) you can ignore — otherwise check tenants miscategorised elsewhere.',
      cta: 'View assigned orgs',
      onAlt: 'drilldown',
    });
  }

  // ── Coverage
  if ((stats.org_count || 0) > 0 && (stats.total_headcount || 0) === 0) {
    fixes.push({
      severity: 'medium',
      dimension: 'Coverage',
      headline: 'Assigned orgs have no headcount on file',
      detail: 'Without total_workforce on each tenant, baseline ROI rolls up as $0 even though the industry is in use. Ask org admins to fill in their headcount.',
      cta: 'View assigned orgs',
      onAlt: 'drilldown',
    });
  } else if ((breakdown.coverage || 0) < 0.3 && (stats.org_count || 0) > 0) {
    fixes.push({
      severity: 'low',
      dimension: 'Coverage',
      headline: 'Small headcount footprint',
      detail: 'Tagged orgs total fewer employees than the library leaders. Score will climb as more or larger tenants adopt this vertical.',
      cta: null,
    });
  }

  return fixes;
}

const SEVERITY_STYLES = {
  high: { dot: 'bg-rose-500', text: 'text-rose-700', border: 'border-rose-200', bg: 'bg-rose-50' },
  medium: { dot: 'bg-amber-500', text: 'text-amber-700', border: 'border-amber-200', bg: 'bg-amber-50' },
  low: { dot: 'bg-slate-400', text: 'text-slate-700', border: 'border-slate-200', bg: 'bg-slate-50' },
};

export default function QuickFixModal({ fix, onClose, onJumpToEditor, onArchive, onDrillDown }) {
  const { profile, score, breakdown, stats } = fix;
  const tier = healthTier(score);
  const fixes = useMemo(
    () => buildFixes(profile, breakdown, stats),
    [profile, breakdown, stats],
  );

  return (
    <div
      role="presentation"
      onClick={onClose}
      data-testid="industry-quickfix-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
    >
      <div
        role="dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[88vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`shrink-0 rounded-lg p-2 ${tier.classes}`}>
              <AlertCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900 truncate">
                Quick fixes for {profile.name}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Score <span className="font-mono font-semibold text-slate-700">{score}</span> · {tier.label}.
                Each fix below directly raises the dimension shown.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="industry-quickfix-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="overflow-y-auto px-6 py-4 flex-1">
          {/* Sub-score bars — gives admins the at-a-glance view before
              they read the fix list. */}
          <div className="grid grid-cols-4 gap-2 mb-4" data-testid="industry-quickfix-bars">
            {['completeness', 'recency', 'adoption', 'coverage'].map((dim) => {
              const v = breakdown[dim] || 0;
              const pct = Math.round(v * 100);
              const barColor = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-rose-500';
              return (
                <div key={dim} className="rounded-lg border border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{dim}</div>
                  <div className="font-mono text-sm font-bold text-slate-800 mb-1">{pct}%</div>
                  <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {fixes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50/40 p-8 text-center text-sm text-emerald-700">
              No specific gaps detected — the score is held back by widely-distributed
              factors (library leaders are simply larger). Score will climb naturally as
              this industry sees more adoption.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="industry-quickfix-list">
              {fixes.map((f, idx) => {
                const sev = SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.low;
                return (
                  <li
                    key={idx}
                    className={`rounded-lg border ${sev.border} ${sev.bg} p-3`}
                    data-testid={`industry-quickfix-item-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <span className={`shrink-0 mt-1.5 h-2 w-2 rounded-full ${sev.dot}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                              {f.dimension}
                            </span>
                            <span className={`text-[10px] font-semibold ${sev.text}`}>
                              · {f.severity} impact
                            </span>
                          </div>
                          <div className="text-sm font-semibold text-slate-900 mt-0.5">
                            {f.headline}
                          </div>
                          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                            {f.detail}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col gap-1.5">
                        {f.cta && f.tab && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onJumpToEditor(f.tab)}
                            data-testid={`industry-quickfix-cta-${idx}`}
                            className="text-xs h-7 whitespace-nowrap"
                          >
                            {f.cta}
                          </Button>
                        )}
                        {/* Adoption / Coverage gaps don't have a tab to
                         *  jump to — they open the drill-down side panel
                         *  so admins can reassign tenants directly. */}
                        {f.cta && !f.tab && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={onDrillDown}
                            data-testid={`industry-quickfix-drilldown-${idx}`}
                            className="text-xs h-7 whitespace-nowrap"
                          >
                            {f.cta}
                          </Button>
                        )}
                        {f.altCta && f.onAlt === 'archive' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={onArchive}
                            data-testid={`industry-quickfix-archive-${idx}`}
                            className="text-xs h-7 whitespace-nowrap text-rose-600 hover:bg-rose-50"
                          >
                            {f.altCta}
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
          <div className="text-[11px] text-slate-500">
            Fixes ordered by biggest impact on the score.
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="industry-quickfix-done">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
